<script setup lang="ts">
import {
  CoreEventSource,
  RemoveHotelSelectionFiltersOrigin,
} from '@apps/main/new-desktop/core/models';
import type { HotelFilterUpdate } from '@apps/main/new-desktop/modules/hotelFilters';
import { useFiltersStore } from '@apps/main/new-desktop/core/stores/filters/filtersStore';
import { useEditingIngredientStore } from '@apps/main/modules/store/editingIngredientStore';

import { AppBackButton } from '@libs/ui';

import FirstDayTripPriceCalendarMobile from '../../../core/components/FirstDayTripPriceCalendarMobile.vue';
import { useRouterStore, useStatsigEventsStore } from '../../../core/stores';
import {
  PriceCalendarTheme,
  ShowPriceCalendarButton,
} from '../../../modules/priceCalendar';
import { HotelSelectionViewType } from '../models';
import {
  useHotelSelectionModalsStore,
  useHotelSelectionStore,
} from '../stores';
import { HotelFiltersTags } from '../../../modules/hotelFilters';

import HotelSelectionDestination from './HotelSelectionDestination.vue';
import HotelSelectionListHeader from './HotelSelectionListHeader.vue';

const hotelSelectionStore = useHotelSelectionStore();
const hotelSelectionModalsStore = useHotelSelectionModalsStore();
const routerStore = useRouterStore();
const statsigEventsStore = useStatsigEventsStore();
const filtersStore = useFiltersStore();
const editingIngredientStore = useEditingIngredientStore();

function onHotelSelectionFilterUpdateFromTags(
  filterUpdate: HotelFilterUpdate
): void {
  filtersStore.updateHotelFilter(
    filterUpdate,
    RemoveHotelSelectionFiltersOrigin.HotelList
  );
}

function onBackButtonClick(): void {
  routerStore.navigateToTripOverview();
  statsigEventsStore.logHotelSelectionBackButtonClicked();
}

function onNavigationTriggered(): void {
  statsigEventsStore.logHotelSelectionBackButtonClicked();
}
</script>

<template>
  <div class="hotel-selection-header-mobile">
    <FirstDayTripPriceCalendarMobile
      :show-month-select-dropdown="true"
      :show-price-calendar="hotelSelectionStore.shouldShowPriceCalendar"
      :source="CoreEventSource.HotelSelection"
      :theme="PriceCalendarTheme.Light"
      :shadow="true"
    >
      <template #left-action>
        <AppBackButton
          type="black"
          :text="$t('planner.back')"
          :link="routerStore.getTripOverviewUrl()"
          font="font-label-l-light"
          @click="onBackButtonClick"
          @navigation-triggered="onNavigationTriggered"
        />
      </template>
      <template #right-action>
        <ShowPriceCalendarButton
          class="show-price-calendar-button"
          @click="hotelSelectionStore.showPriceCalendar()"
        />
      </template>
      <template #bottom-row>
        <div class="bottom-row">
          <HotelSelectionDestination
            v-if="hotelSelectionStore.hotelSelectionHeaderData"
            class="hotel-selection-destination"
            :data="hotelSelectionStore.hotelSelectionHeaderData"
            @open-filters="hotelSelectionStore.openFilters()"
            @change-number-of-nights="hotelSelectionStore.changeNumberOfNights"
            @change-destination="
              hotelSelectionModalsStore.openDestinationSelectorModal(
                $event,
                hotelSelectionStore.editingIngredientId
              )
            "
          />
          <HotelFiltersTags
            v-if="
              hotelSelectionStore.viewType === HotelSelectionViewType.List &&
              filtersStore.hasActiveHotelFilters()
            "
            :ingredient="editingIngredientStore.editingStayIngredient"
            class="tags"
            @update-filter="onHotelSelectionFilterUpdateFromTags"
          />
          <HotelSelectionListHeader
            v-if="hotelSelectionStore.viewType === HotelSelectionViewType.List"
            :hotels-total="hotelSelectionStore.hotelSelectionCards.length"
            :suggestion-type="hotelSelectionStore.listSuggestionType"
            class="list-header"
            @suggestion-type-selected="
              hotelSelectionStore.setListSuggestionType
            "
            @show-why-we-suggest="
              hotelSelectionModalsStore.openWhyWeSuggestModal
            "
          />
        </div>
      </template>
    </FirstDayTripPriceCalendarMobile>
  </div>
</template>

<style scoped lang="scss">
.hotel-selection-header-mobile {
  position: sticky;
  top: 0;
  z-index: 3;

  .show-price-calendar-button {
    color: var(--color-secondary-40);
  }

  .hotel-selection-destination {
    background-color: var(--color-quaternary-70);
    padding: var(--spacing-16);
  }

  .tags {
    overflow-x: auto;
    border-bottom: 1px solid var(--color-secondary-90);
    margin: 0 var(--spacing-16) var(--spacing-8) var(--spacing-16);
    background-color: var(--color-quaternary-70);
  }

  .list-header {
    padding: 0 var(--spacing-16) var(--spacing-8) var(--spacing-16);
  }
  .bottom-row {
    background-color: var(--color-quaternary-70);
  }
}
</style>
